/**
 * Flutter 앱 자동 로그인 플러그인
 *
 * WebView 캐시가 삭제되어도 앱에 저장된 Custom Token을 사용하여
 * 자동으로 로그인을 복구합니다.
 *
 * 흐름:
 * 1. Flutter 앱 환경인지 확인
 * 2. Firebase 자동 로그인 실패 시 (쿠키 삭제 상태)
 * 3. 앱에서 저장된 Custom Token 요청
 * 4. Custom Token에서 UID 추출 → 서버에 새 토큰 요청 → 로그인
 */
import { signInWithCustomToken } from 'firebase/auth'
import { getFirebaseAuth } from '@/services/firebaseService'
import { useFlutterBridge, extractUidFromToken } from '@/composables/flutter/useFlutterBridge'
import { useApi } from '@/composables/useApi'

export default defineNuxtPlugin(async () => {
  const { isFlutterApp, requestCustomToken, saveCustomToken, sendWebReady } = useFlutterBridge()

  // Flutter 앱이 아니면 건너뛰기
  if (!isFlutterApp.value) {
    return
  }

  console.log('🔄 [FlutterAuth] Flutter 앱 자동 로그인 플러그인 시작')

  const auth = getFirebaseAuth()

  // 이미 로그인되어 있으면 건너뛰기
  if (auth.currentUser) {
    console.log('✅ [FlutterAuth] 이미 로그인됨 - 건너뛰기')
    sendWebReady()
    return
  }

  // 앱에서 저장된 Custom Token 요청
  const savedToken = await requestCustomToken()

  if (savedToken) {
    console.log('📤 [FlutterAuth] 저장된 Custom Token 확인됨 - UID 추출 시도')

    // Custom Token에서 UID 추출 → 바로 새 토큰 요청
    // (저장된 토큰은 99% 만료되었으므로 로그인 시도 생략)
    const uid = extractUidFromToken(savedToken)

    if (uid) {
      console.log('🔑 [FlutterAuth] UID 추출 성공 - 새 토큰 요청 중...')

      try {
        const api = useApi()
        const response = await api.post('/api/auth/issue-custom-token-by-uid', { uid })

        if (response.data?.isSuccess && response.data?.customToken) {
          await signInWithCustomToken(auth, response.data.customToken)
          // 새 토큰 저장
          await saveCustomToken(response.data.customToken)
          console.log('✅ [FlutterAuth] 자동 로그인 성공')
        } else {
          console.error('❌ [FlutterAuth] 토큰 재발급 실패:', response.data?.message)
        }
      } catch (error) {
        console.error('❌ [FlutterAuth] 자동 로그인 실패:', error)
      }
    } else {
      console.error('❌ [FlutterAuth] UID 추출 실패')
    }
  } else {
    console.log('📭 [FlutterAuth] 저장된 Custom Token 없음 - 로그인 필요')
  }

  sendWebReady()
})
